import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, AlertCircle, ArrowRight, Wand2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, getAuth } from 'firebase/auth';

export default function Login() {
    const { signIn, signInWithGoogle, sendMagicLink, signInWithMagicLink, resetPassword, error: authError } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showReset, setShowReset] = useState(false);
    const [useMagicLink, setUseMagicLink] = useState(false);
    const [resetSent, setResetSent] = useState(false);
    const [magicLinkSent, setMagicLinkSent] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const navigate = useNavigate();

    // Check for Magic Link on mount
    useEffect(() => {
        const auth = getAuth();
        if (isSignInWithEmailLink(auth, window.location.href)) {
            let emailForSignIn = window.localStorage.getItem('emailForSignIn');
            if (!emailForSignIn) {
                // If email is not in storage (different device), user must provide it
                emailForSignIn = window.prompt('Please provide your email for confirmation');
            }

            if (emailForSignIn) {
                setLoading(true);
                signInWithMagicLink(emailForSignIn, window.location.href)
                    .then(() => {
                        navigate('/dashboard'); // Or let AuthContext/App handle redirect based on user state
                    })
                    .catch((err) => {
                        console.error(err);
                        setLocalError('Failed to sign in with magic link. Link may be expired.');
                        setLoading(false);
                    });
            }
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setLocalError(null);
        try {
            if (useMagicLink) {
                await sendMagicLink(email);
                setMagicLinkSent(true);
            } else {
                await signIn(email, password);
            }
        } catch (err) {
            // Error is handled in context
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setLoading(true);
        setLocalError(null);
        try {
            await signInWithGoogle();
        } catch (err) {
            // Error is handled in context
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await resetPassword(email);
            setResetSent(true);
        } catch (err) {
            // Error is handled in context
        } finally {
            setLoading(false);
        }
    };

    const error = authError || localError;

    return (
        <div className="min-h-screen flex items-center justify-center p-6">
            {/* Background decoration */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-radial from-primary-500/20 to-transparent opacity-50" />
                <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-radial from-accent-500/20 to-transparent opacity-50" />
            </div>

            <div className="w-full max-w-md relative">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-linear-to-br from-primary-500 to-accent-500 mb-4 shadow-glow">
                        <span className="text-white font-bold text-2xl">A</span>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
                    <p className="text-gray-400">Sign in to your affiliate account</p>
                </div>

                {/* Login Card */}
                <div className="card">
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400">
                            <AlertCircle size={20} />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    {!showReset ? (
                        <>
                            {magicLinkSent ? (
                                <div className="text-center py-4">
                                    <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Wand2 className="text-primary-400" size={32} />
                                    </div>
                                    <h3 className="text-xl font-semibold text-white mb-2">Check your email</h3>
                                    <p className="text-gray-400 mb-6">
                                        We've sent a magic link to {email}.<br />Click it to sign in instantly.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMagicLinkSent(false);
                                            setUseMagicLink(false);
                                        }}
                                        className="btn-secondary"
                                    >
                                        Back to Sign In
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Google Sign In */}
                                    <button
                                        type="button"
                                        onClick={handleGoogleSignIn}
                                        disabled={loading}
                                        className="w-full btn-secondary flex items-center justify-center gap-2 bg-white text-gray-900 hover:bg-gray-100 border-none"
                                    >
                                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                                            <path
                                                fill="currentColor"
                                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                            />
                                            <path
                                                fill="currentColor"
                                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                            />
                                            <path
                                                fill="currentColor"
                                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                            />
                                            <path
                                                fill="currentColor"
                                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                            />
                                        </svg>
                                        Sign in with Google
                                    </button>

                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-gray-700"></div>
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="px-2 bg-gray-900 text-gray-400">Or continue with</span>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Email Address
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="input-field pl-12"
                                                placeholder="you@company.com"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {!useMagicLink && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Password
                                            </label>
                                            <div className="relative">
                                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                                <input
                                                    type="password"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    className="input-field pl-12"
                                                    placeholder="••••••••"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setUseMagicLink(!useMagicLink);
                                                setLocalError(null);
                                            }}
                                            className="text-sm text-primary-400 hover:text-primary-300"
                                        >
                                            {useMagicLink ? 'Use password instead' : 'Use magic link instead'}
                                        </button>

                                        {!useMagicLink && (
                                            <button
                                                type="button"
                                                onClick={() => setShowReset(true)}
                                                className="text-sm text-gray-400 hover:text-white"
                                            >
                                                Forgot password?
                                            </button>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn-primary w-full flex items-center justify-center gap-2"
                                    >
                                        {loading ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                {useMagicLink ? 'Send Magic Link' : 'Sign In'}
                                                <ArrowRight size={20} />
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </>
                    ) : (
                        <form onSubmit={handleResetPassword} className="space-y-6">
                            {resetSent ? (
                                <div className="text-center py-4">
                                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Mail className="text-emerald-400" size={32} />
                                    </div>
                                    <h3 className="text-xl font-semibold text-white mb-2">Check your email</h3>
                                    <p className="text-gray-400 mb-6">
                                        We've sent password reset instructions to {email}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowReset(false);
                                            setResetSent(false);
                                        }}
                                        className="btn-secondary"
                                    >
                                        Back to Sign In
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="text-center mb-4">
                                        <h3 className="text-xl font-semibold text-white mb-2">Reset Password</h3>
                                        <p className="text-gray-400 text-sm">
                                            Enter your email and we'll send you reset instructions
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Email Address
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="input-field pl-12"
                                                placeholder="you@company.com"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setShowReset(false)}
                                            className="btn-secondary flex-1"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="btn-primary flex-1"
                                        >
                                            {loading ? 'Sending...' : 'Send Reset Link'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </form>
                    )}
                </div>

                <p className="text-center text-gray-500 text-sm mt-6">
                    Need an account? Contact your administrator.
                </p>
            </div>
        </div>
    );
}
